import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, User, LogOut, Apple, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showShopMenu, setShowShopMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cartCount } = useCart();

  const navLinks = [
    { name: 'Nutrition', path: '/nutrition' },
    { name: 'Media', path: '/media' },
    { name: 'Fashion', path: '/fashion' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_build-site-461/artifacts/if5761vx_Arteryvail-New-Logo-removebg-preview.png" 
              alt="ARTERYVAIL" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-12">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm tracking-widest uppercase transition-colors duration-300 ${
                  location.pathname.startsWith(link.path)
                    ? 'text-[#E5D3B3]'
                    : 'text-[#A3988F] hover:text-[#F5F2EB]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-6">
            {/* Shop Menu with Hover */}
            <div 
              className="relative"
              onMouseEnter={() => setShowShopMenu(true)}
              onMouseLeave={() => setShowShopMenu(false)}
            >
              <Link to="/cart" className="relative flex items-center" data-testid="nav-cart">
                <ShoppingBag className="w-5 h-5 text-[#A3988F] hover:text-[#F5F2EB] transition-colors" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#E5D3B3] text-[#0A0A0A] text-xs rounded-full flex items-center justify-center font-semibold">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              <AnimatePresence>
                {showShopMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-56 bg-[#1A1514] border border-[#E5D3B3]/20 rounded-lg overflow-hidden shadow-xl"
                  >
                    <div className="p-3 border-b border-[#E5D3B3]/10">
                      <p className="text-xs tracking-widest uppercase text-[#A3988F]">Shop Our Collections</p>
                    </div>
                    <Link
                      to="/nutrition/shop"
                      className="flex items-center space-x-3 px-4 py-3 text-sm text-[#F5F2EB] hover:bg-[#3D2314] transition-colors"
                      data-testid="shop-nutrition-link"
                    >
                      <Apple className="w-4 h-4 text-[#E5D3B3]" />
                      <span>Shop Nutrition</span>
                    </Link>
                    <Link
                      to="/fashion"
                      className="flex items-center space-x-3 px-4 py-3 text-sm text-[#F5F2EB] hover:bg-[#3D2314] transition-colors"
                      data-testid="shop-fashion-link"
                    >
                      <Sparkles className="w-4 h-4 text-[#8C7B6B]" />
                      <span>Shop Fashion</span>
                    </Link>
                    {cartCount > 0 && (
                      <Link
                        to="/cart"
                        className="flex items-center justify-between px-4 py-3 text-sm bg-[#3D2314] text-[#E5D3B3]"
                        data-testid="view-cart-link"
                      >
                        <span>View Cart</span>
                        <span className="bg-[#E5D3B3] text-[#0A0A0A] text-xs px-2 py-0.5 rounded-full">{cartCount} items</span>
                      </Link>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 text-[#A3988F] hover:text-[#F5F2EB] transition-colors"
                  data-testid="nav-user-menu"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden lg:inline text-sm">{user.name}</span>
                </button>
                
                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-4 w-48 bg-[#1A1514] border border-[#E5D3B3]/20 rounded-lg overflow-hidden"
                    >
                      <Link
                        to="/dashboard"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-3 text-sm text-[#F5F2EB] hover:bg-[#3D2314] transition-colors"
                        data-testid="nav-dashboard"
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-3 text-sm text-[#F5F2EB] hover:bg-[#3D2314] transition-colors"
                        data-testid="nav-orders"
                      >
                        My Orders
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 text-sm text-[#FAD6C6] hover:bg-[#3D2314] transition-colors flex items-center space-x-2"
                        data-testid="nav-logout"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-sm tracking-widest uppercase text-[#A3988F] hover:text-[#F5F2EB] transition-colors"
                data-testid="nav-login"
              >
                Login
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-[#F5F2EB]"
              data-testid="nav-mobile-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-[#E5D3B3]/10"
          >
            <div className="px-6 py-8 space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block text-lg tracking-wider text-[#F5F2EB] hover:text-[#E5D3B3] transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-[#E5D3B3]/10 pt-6 space-y-4">
                <Link
                  to="/nutrition/shop"
                  onClick={() => setIsOpen(false)}
                  className="block text-[#E5D3B3]"
                >
                  Shop Nutrition
                </Link>
                <Link
                  to="/fashion"
                  onClick={() => setIsOpen(false)}
                  className="block text-[#8C7B6B]"
                >
                  Shop Fashion
                </Link>
              </div>
              {!user && (
                <Link
                  to="/login"
                  onClick={() => setIsOpen(false)}
                  className="block text-lg tracking-wider text-[#E5D3B3]"
                >
                  Login
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
